import { useRef, useState } from 'react';
import { ActionIcon, Button, Group, Loader, Modal, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconBuildingBank, IconCircleCheck, IconFileUpload, IconPencil, IconPlus, IconRefresh } from '@tabler/icons-react';
import { collection, deleteField, doc, DocumentReference, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getMetadata, StorageReference } from 'firebase/storage';
import { UploadBox } from './upload_box';
import { uploadToStorage } from '../helpers/uploadToStorage';
import { notifications } from '@mantine/notifications';
import { useFirestore } from '../helpers/firebaseContext';
import { CustomClaims } from '../types';
import { CustomInfoCard } from '../routes/customers/CustomInfoCard';
import { generateIBANFromKFHAccountNo, isValidKfhIban } from '../utils';

interface IbanInfoCardProps {
    acct?: string;
    customerRef: DocumentReference;
    entityLabel: string;
    entityRef: DocumentReference;
    iban?: string;
    ibanVerified?: boolean;
    userClaims: CustomClaims;
    verifiedBy?: string;
    verifierIdentity: string;
    /** show Set / Edit IBAN (KFH IBAN or 12-digit KFH account number) */
    allowSetIban?: boolean;
    /** show "Sync from files" while the IBAN or account number is missing */
    onSyncFromFiles?: () => Promise<void>;
    /** kfhtrade: only a KFH IBAN that passes the check digits, with its matching account number, can be verified */
    requireValidKfhIban?: boolean;
    /** kfhtrade: storage folder to upload a transfer certificate to; its OCR'd IBAN is shown to confirm */
    certificateUploadPath?: string;
    onCertificateUploaded?: () => void;
}

//the upload trigger OCRs the file and stores a valid KFH IBAN on its metadata (fillKfhtradeServiceIban)
const waitForCertificateIban = async (fileRef: StorageReference, isCancelled: () => boolean) => {
    for (let attempt = 0; attempt < 30 && !isCancelled(); attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const read = (await getMetadata(fileRef)).customMetadata?.iban;
        if (isValidKfhIban(read)) return read;
    }
    return undefined;
};

export const IbanInfoCard = ({
    acct,
    customerRef,
    entityLabel,
    entityRef,
    iban,
    ibanVerified,
    userClaims,
    verifiedBy,
    verifierIdentity,
    allowSetIban,
    onSyncFromFiles,
    requireValidKfhIban,
    certificateUploadPath,
    onCertificateUploaded,
}: IbanInfoCardProps) => {
    const db = useFirestore();
    const [verifyingIban, setVerifyingIban] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [setIbanOpened, setSetIbanOpened] = useState(false);
    const [ibanInput, setIbanInput] = useState('');
    const [acctInput, setAcctInput] = useState('');
    const [ibanInputError, setIbanInputError] = useState<string | null>(null);
    const [savingIban, setSavingIban] = useState(false);
    const [certStep, setCertStep] = useState<'closed' | 'upload' | 'reading' | 'read' | 'notFound'>('closed');
    const [certIban, setCertIban] = useState('');
    const [certSaving, setCertSaving] = useState(false);
    const certCancelled = useRef(false);

    const canEditIban = Boolean(userClaims.admin || userClaims.cr || userClaims.kfh_rm || userClaims.kfh_oper);
    //verifying also writes customers/{c}/accounts, which the rules allow for admin / cr / oper only
    const canWriteVerification = Boolean(userClaims.admin || userClaims.cr || userClaims.oper);
    const ibanFailsKfhCheck = Boolean(requireValidKfhIban && iban && !(isValidKfhIban(iban) && acct === iban.slice(-12)));
    const canVerifyIban = Boolean(
        iban
        && ibanVerified !== true
        && !ibanFailsKfhCheck
        && canWriteVerification,
    );
    const canSetIban = Boolean(allowSetIban && canEditIban);
    const canSync = Boolean(onSyncFromFiles && (!iban || !acct) && canEditIban);

    const handleSync = async () => {
        if (!onSyncFromFiles) return;
        try {
            setSyncing(true);
            await onSyncFromFiles();
        } finally {
            setSyncing(false);
        }
    };

    //same as add order: either field fills the other on blur
    const normalizedIbanInput = () => ibanInput.replace(/\s+/g, '').toUpperCase();
    const normalizedAcctInput = () => acctInput.replace(/\s+/g, '');
    const handleIbanBlur = () => {
        const value = normalizedIbanInput();
        if (isValidKfhIban(value)) setAcctInput(value.slice(-12));
    };
    const handleAcctBlur = () => {
        const value = normalizedAcctInput();
        if (/^\d{12}$/.test(value)) setIbanInput(generateIBANFromKFHAccountNo(value) || '');
    };

    const handleSetIban = async () => {
        //a KFH IBAN, or one built from the 12-digit KFH account number
        const acctValue = normalizedAcctInput();
        const newIban = normalizedIbanInput() || (/^\d{12}$/.test(acctValue) ? generateIBANFromKFHAccountNo(acctValue) : '');
        if (!isValidKfhIban(newIban)) {
            setIbanInputError('Enter a valid KFH IBAN or 12-digit KFH account number');
            return;
        }
        const newAcct = newIban.slice(-12);
        if (acctValue && acctValue !== newAcct) {
            setIbanInputError(`The account number doesn't match this IBAN (expected ${newAcct})`);
            return;
        }

        try {
            setSavingIban(true);
            const batch = writeBatch(db);
            //a new IBAN always needs to be verified again
            batch.update(entityRef, {
                iban: newIban,
                acct: newAcct,
                ibanVerified: false,
                verifiedBy: deleteField(),
                ibanSetBy: verifierIdentity,
                ibanSetAt: serverTimestamp(),
            });
            await batch.commit();
            notifications.show({
                title: 'IBAN saved',
                message: `The ${entityLabel} IBAN was set to ${newIban}. Verify it with "IBAN is correct".`,
                color: 'green',
            });
            setSetIbanOpened(false);
        } catch (error: any) {
            console.error("Failed to set IBAN", error);
            notifications.show({
                title: 'Failed to set IBAN',
                message: error?.message ?? 'Please try again.',
                color: 'red',
            });
        } finally {
            setSavingIban(false);
        }
    };

    //marks the IBAN verified on the entity and the customer's bank account; extra fields go on the entity
    const writeVerifiedIban = async (verifiedIban: string, verifiedAcct?: string, extra: Record<string, unknown> = {}) => {
        const batch = writeBatch(db);
        batch.update(entityRef, { ...extra, ibanVerified: true, verifiedBy: verifierIdentity });
        batch.set(doc(collection(customerRef, "accounts"), verifiedIban), {
            iban: verifiedIban,
            verified: true,
            verifiedBy: verifierIdentity,
            ...(verifiedAcct ? { account: verifiedAcct } : {}),
        }, { merge: true });
        await batch.commit();
    };

    const closeCertificate = () => {
        certCancelled.current = true;
        setCertStep('closed');
    };

    const handleCertificateDrop = async (files?: File[]) => {
        if (!files?.length || !certificateUploadPath) return;
        certCancelled.current = false;
        setCertStep('reading');
        try {
            const uploaded = await uploadToStorage(files[0], certificateUploadPath, { transferCert: 'true' });
            onCertificateUploaded?.();
            const read = uploaded && await waitForCertificateIban(uploaded.ref, () => certCancelled.current);
            if (certCancelled.current) return;
            if (read) {
                setCertIban(read);
                setCertStep('read');
            } else {
                setCertStep('notFound');
            }
        } catch (error: any) {
            console.error("Failed to upload the transfer certificate", error);
            notifications.show({ title: 'Upload failed', message: error?.message ?? 'Please try again.', color: 'red' });
            setCertStep('upload');
        }
    };

    //CR saw what OCR read and confirmed it, so it's saved as verified
    const confirmCertificateIban = async () => {
        try {
            setCertSaving(true);
            await writeVerifiedIban(certIban, certIban.slice(-12), {
                iban: certIban,
                acct: certIban.slice(-12),
                ibanSetBy: verifierIdentity,
                ibanSetAt: serverTimestamp(),
            });
            notifications.show({ title: 'IBAN verified', message: `${certIban} is saved and verified.`, color: 'green' });
            closeCertificate();
            onCertificateUploaded?.();
        } catch (error: any) {
            console.error("Failed to save the certificate IBAN", error);
            notifications.show({ title: 'Failed to save the IBAN', message: error?.message ?? 'Please try again.', color: 'red' });
        } finally {
            setCertSaving(false);
        }
    };

    const handleVerifyIban = async () => {
        if (ibanFailsKfhCheck) {
            notifications.show({
                title: 'Invalid IBAN',
                message: 'Only a KFH IBAN with valid check digits and its matching account number can be verified. Edit it first.',
                color: 'red',
            });
            return;
        }
        if (!iban) {
            notifications.show({
                title: 'No IBAN to verify',
                message: `This ${entityLabel} does not have an IBAN yet.`,
                color: 'yellow',
            });
            return;
        }

        try {
            setVerifyingIban(true);
            //kfhtrade: pin the IBAN being verified, so an edit made meanwhile by someone else can't get verified instead
            await writeVerifiedIban(iban, acct, requireValidKfhIban ? { iban, acct } : {});
            notifications.show({
                title: 'IBAN verified',
                message: `The ${entityLabel} IBAN and customer bank account have been marked as verified.`,
                color: 'green',
            });
        } catch (error: any) {
            console.error("Failed to verify IBAN", error);
            notifications.show({
                title: 'Failed to verify IBAN',
                message: error?.message ?? 'Please try again.',
                color: 'red',
            });
        } finally {
            setVerifyingIban(false);
        }
    };

    return (
        <>
        <Modal opened={setIbanOpened} onClose={() => setSetIbanOpened(false)} title={iban ? 'Edit IBAN' : 'Set IBAN'}>
            <TextInput
                label="KFH IBAN"
                placeholder="KW.. KFHO ...."
                value={ibanInput}
                error={ibanInputError}
                onChange={(event) => {
                    setIbanInput(event.currentTarget.value);
                    setIbanInputError(null);
                }}
                onBlur={handleIbanBlur}
                data-autofocus
            />
            <TextInput
                mt="sm"
                label="KFH account number"
                description="12 digits, fills the IBAN"
                placeholder="012345678901"
                value={acctInput}
                onChange={(event) => {
                    setAcctInput(event.currentTarget.value);
                    setIbanInputError(null);
                }}
                onBlur={handleAcctBlur}
            />
            <Group justify="flex-end" mt="md">
                <Button loading={savingIban} onClick={handleSetIban}>Save</Button>
            </Group>
        </Modal>
        <Modal opened={certStep !== 'closed'} onClose={closeCertificate} title="Upload transfer certificate (IBAN certificate)" size="lg">
            {certStep === 'upload' && (
                <UploadBox
                    label="Drag the transfer certificate here or click to select file"
                    multiple={false}
                    onDropAny={(files) => { void handleCertificateDrop(files); }}
                />
            )}
            {certStep === 'reading' && (
                <Group>
                    <Loader size="sm" />
                    <Text size="sm">Reading the IBAN from the certificate, this can take up to a minute…</Text>
                </Group>
            )}
            {certStep === 'read' && (
                <Stack gap="xs">
                    <Text size="sm">This is what was read from the certificate. Check it against the file:</Text>
                    <Text fw={600}>IBAN: {certIban}</Text>
                    <Text fw={600}>Account: {certIban.slice(-12)}</Text>
                    <Group justify="flex-end" mt="md">
                        <Button
                            variant="default"
                            onClick={() => {
                                notifications.show({
                                    title: 'Left unverified',
                                    message: 'Nothing was verified. Use Set / Edit IBAN to enter the right one.',
                                    color: 'yellow',
                                });
                                closeCertificate();
                            }}
                        >
                            Not correct
                        </Button>
                        <Button color="green" loading={certSaving} onClick={confirmCertificateIban}>Correct, verify it</Button>
                    </Group>
                </Stack>
            )}
            {certStep === 'notFound' && (
                <Stack gap="xs">
                    <Text size="sm">No KFH IBAN could be read from this certificate. Use Set IBAN to enter it.</Text>
                    <Group justify="flex-end"><Button onClick={closeCertificate}>Close</Button></Group>
                </Stack>
            )}
        </Modal>
        <CustomInfoCard
            title="Bank Account Information"
            color="red"
            attention={ibanVerified === false || (requireValidKfhIban && !iban) || ibanFailsKfhCheck}
            icon={<IconBuildingBank size={16} />}
            headerAction={(canVerifyIban || canSetIban || canSync) ? (
                <Group gap={6}>
                    {canSetIban && canWriteVerification && certificateUploadPath && (
                        <Tooltip label="Upload transfer certificate">
                            <ActionIcon variant="light" aria-label="Upload transfer certificate" onClick={() => setCertStep('upload')}>
                                <IconFileUpload size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {canSync && (
                        <Tooltip label="Read the IBAN from the service files">
                            <ActionIcon variant="light" aria-label="Sync IBAN from files" loading={syncing} onClick={handleSync}>
                                <IconRefresh size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {canSetIban && (
                        <Tooltip label={iban ? 'Edit IBAN' : 'Set IBAN'}>
                            <ActionIcon
                                variant="light"
                                aria-label={iban ? 'Edit IBAN' : 'Set IBAN'}
                                onClick={() => {
                                    setIbanInput(iban ?? '');
                                    setAcctInput(acct ?? '');
                                    setIbanInputError(null);
                                    setSetIbanOpened(true);
                                }}
                            >
                                {iban ? <IconPencil size={16} /> : <IconPlus size={16} />}
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {canVerifyIban && (
                        <Tooltip label="IBAN is correct (verify)">
                            <ActionIcon color="green" variant="filled" aria-label="IBAN is correct" loading={verifyingIban} onClick={handleVerifyIban}>
                                <IconCircleCheck size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
            ) : undefined}
            list={[
                {
                    label: 'IBAN',
                    value: iban,
                    noCapitalize: true,
                },
                {
                    label: 'Account',
                    value: acct,
                    noCapitalize: true,
                },
                ...(ibanFailsKfhCheck ? [{
                    label: 'IBAN Check',
                    value: 'Invalid KFH IBAN / account number, edit it',
                    noCapitalize: true,
                }] : []),
                {
                    label: 'Verified',
                    value: ibanVerified,
                    noCapitalize: true,
                },
                {
                    label: 'Verified By',
                    value: verifiedBy,
                    noCapitalize: true,
                },
            ]}
        />
        </>
    );
};
